import React, { useEffect } from "react";
import {
  Invoice,
  InvoiceStatus,
  PaymentsInvoiceAutoCollectionStatus
} from "../../../Contracts/Invoice";
import { ReactComponent as KebabMenuIcon } from "../../../Assets/ActionIcons/KebabMenuIcon.svg";
import { ReactComponent as CloseIcon } from "../../../Assets/ActionIcons/CloseIcon.svg";
import { ReactComponent as InfoIcon } from "../../../Assets/ActionIcons/InfoIcon.svg";
import { ReactComponent as NoAvailablePaymentMethod } from "../../../Assets/CustomerDetailPage/NoAvailablePaymentMethod.svg";
import { useInvoiceStore } from "../../../Store/Invoice";
import { useAuth0 } from "@auth0/auth0-react";
import { useCustomerStore } from "../../../Store/Customer";
import LoadingPage from "../../../Pages/LoadingPage";
import { PaymentMethodDisplayAttributes } from "../../../Contracts/Finance";
import Dropdown from "../../Atoms/Dropdown";
import { capitalizeAfterSpace } from "../../../Contracts/Common";
import { PaymentRoutes } from "../../../Contracts/Routes";

export interface InvoiceListActionsProps {
  invoice: Invoice;
  open: boolean;
  onClose: () => void;
  onOpen: () => void;
}

const InvoiceListActions: React.FC<InvoiceListActionsProps> = ({
  invoice,
  open,
  onClose,
  onOpen
}) => {
  const {
    sendInvoicePaymentLink,
    getInvoicePdf,
    toggleEmailOrAutoCollectSettings,
    collectNow,
    syncInvoiceNow
  } = useInvoiceStore();

  const { customerDetailEntity, getCustomerDetails } = useCustomerStore();
  const { getAccessTokenSilently } = useAuth0();

  const onSendPaymentLink = async (customerId: number, invoiceId: number) => {
    onClose();
    if (window.confirm("Are you sure you want to send payment link?")) {
      await sendInvoicePaymentLink(
        getAccessTokenSilently,
        customerId.toString(),
        invoiceId.toString()
      );
      setInformationDialogue({
        open: true,
        message: "Payment link sent successfully"
      });
    }
  };

  const onPdfView = async (customerId: number, invoiceId: number) => {
    onClose();
    const pdfUrl = await getInvoicePdf(customerId.toString(), invoiceId.toString());
    window.open(pdfUrl);
  };
  const existingEmailEnabled = !!invoice.invoice_emails_enabled;
  const existingAutoCollectEnabled =
    invoice.payments_invoice_auto_collection_status === PaymentsInvoiceAutoCollectionStatus.Pending;

  const emailEnableButtonMessage =
    existingEmailEnabled && existingAutoCollectEnabled
      ? "disable Invoice Emails and Auto Collect"
      : existingEmailEnabled && !existingAutoCollectEnabled
        ? "disable Invoice Emails"
        : "enable Invoice Emails";

  const autoCollectEnableButtonMessage =
    existingAutoCollectEnabled && existingAutoCollectEnabled
      ? "disable Auto Collect"
      : existingEmailEnabled && !existingAutoCollectEnabled
        ? "enable Auto Collect"
        : "enable Auto Collect and Invoice Emails";

  const onToggleEmailOrAutoCollectSettings = async (
    toggled: "emailButton" | "autoCollectButton"
  ) => {
    let emailsEnabled = existingEmailEnabled;
    let autoCollectEnabled = existingAutoCollectEnabled;

    if (toggled === "emailButton") {
      emailsEnabled = !existingEmailEnabled && !existingAutoCollectEnabled;
      autoCollectEnabled = !existingEmailEnabled && existingAutoCollectEnabled;
    } else if (toggled === "autoCollectButton") {
      emailsEnabled = existingEmailEnabled || !existingAutoCollectEnabled;
      autoCollectEnabled =
        !existingEmailEnabled && !existingAutoCollectEnabled
          ? true
          : existingEmailEnabled && !existingAutoCollectEnabled;
    }
    if (
      window.confirm(
        `Are you sure you want to ${
          toggled === "emailButton" ? emailEnableButtonMessage : autoCollectEnableButtonMessage
        } for Invoice ${invoice.invoice_number}?`
      )
    ) {
      await toggleEmailOrAutoCollectSettings(
        getAccessTokenSilently,
        invoice.customer_id.toString(),
        invoice.id.toString(),
        emailsEnabled,
        autoCollectEnabled
      );
      onClose();
    }
  };

  const moreMenuRef = React.useRef<HTMLDivElement>(null);
  const informationDialogueRef = React.useRef<HTMLDivElement>(null);
  const collectNowModalRef = React.useRef<HTMLDivElement>(null);
  const [informationDialogue, setInformationDialogue] = React.useState<{
    open: boolean;
    message?: string;
  }>({ open: false });
  const [collectNowModal, setCollectNowModal] = React.useState<{
    open: boolean;
    isLoading: boolean;
    selectedToken?: string;
  }>({ open: false, isLoading: false });

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (moreMenuRef.current && !moreMenuRef.current.contains(event.target as Node)) {
        onClose();
      }
      if (
        informationDialogueRef.current &&
        !informationDialogueRef.current.contains(event.target as Node)
      ) {
        setInformationDialogue({ open: false });
      }
      if (
        collectNowModalRef.current &&
        !collectNowModalRef.current.contains(event.target as Node)
      ) {
        setCollectNowModal({ open: false, isLoading: false });
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moreMenuRef, informationDialogueRef, collectNowModalRef]);

  const onCollectNowModalOpen = async () => {
    setCollectNowModal({ open: true, isLoading: true });
    await getCustomerDetails(getAccessTokenSilently, invoice.customer_id.toString());
    setCollectNowModal({ open: true, isLoading: false });
  };

  const collectNowAvailablePaymentMethods =
    customerDetailEntity?.available_payment_methods?.data
      .sort((a, _) => (a.primary ? -1 : 1))
      .map(m => new PaymentMethodDisplayAttributes(m)) || [];

  const onCollectNowRequest = async () => {
    if (!collectNowModal.selectedToken) {
      window.alert("Please select a payment method to proceed");
      return;
    }
    setCollectNowModal({ ...collectNowModal, isLoading: true });
    const response = await collectNow(
      getAccessTokenSilently,
      invoice.id.toString(),
      collectNowModal.selectedToken
    );
    setInformationDialogue({ open: true, message: response.message });
    setCollectNowModal({ open: false, isLoading: false });
  };

  const onShowReceipt = async () => {
    onClose();
    if (invoice.customer_id && invoice.adyen_transaction?.id) {
      window.open(
        PaymentRoutes.INVOICE_RECEIPT.replace(
          ":customer_id",
          invoice.customer_id.toString()
        ).replace(":transaction_id", invoice.adyen_transaction.id.toString()),
        "_blank"
      );
    } else {
      window.alert("Receipt not found!");
    }
  };

  const onChange = async (selectedValue: string | number) => {
    switch (selectedValue) {
      case "send_payment_link":
        await onSendPaymentLink(invoice.customer_id, invoice.id);
        break;
      case "email_enable":
        await onToggleEmailOrAutoCollectSettings("emailButton");
        break;
      case "auto_collect_enable":
        await onToggleEmailOrAutoCollectSettings("autoCollectButton");
        break;
      case "view_pdf":
        await onPdfView(invoice.customer_id, invoice.id);
        break;
      case "pay_now":
        window.open(
          `${window.origin}${PaymentRoutes.CHECKOUT_INVOICE_PAY.replace(
            ":customer_id",
            invoice.customer_id.toString()
          ).replace(":invoice_id", invoice.id.toString())}`
        );
        break;
      case "collect_now":
        await onCollectNowModalOpen();
        break;
      case "sync_invoice":
        await syncInvoiceNow(
          getAccessTokenSilently,
          invoice.customer_id.toString(),
          invoice.id.toString()
        );
        break;
      case "show_receipt":
        await onShowReceipt();
        break;
      default:
        break;
    }
  };

  const actions = (): {
    label: string;
    value: string;
    disabled: boolean;
  }[] => {
    let actions = [];
    actions.push({
      label: "Send Payment Link",
      value: "send_payment_link",
      disabled: invoice.status === InvoiceStatus.Paid || invoice.status === InvoiceStatus.Voided,
      disabledMessage:
        invoice.status === InvoiceStatus.Paid
          ? "Invoice is paid"
          : invoice.status === InvoiceStatus.Voided
            ? "Invoice is voided"
            : "",
      disabledMessagePosition: "top"
    });
    if (invoice.status === InvoiceStatus.Paid || invoice.status === InvoiceStatus.Voided) {
      actions.push({
        label: "Auto-collect not available",
        value: "auto_collect_not_available",
        disabled: true,
        disabledMessage:
          invoice.status === InvoiceStatus.Paid
            ? "Invoice is paid"
            : invoice.status === InvoiceStatus.Voided
              ? "Invoice is voided"
              : ""
      });
    } else {
      actions.push({
        label: capitalizeAfterSpace(emailEnableButtonMessage),
        value: "email_enable",
        disabled: false
      });
      actions.push({
        label: capitalizeAfterSpace(autoCollectEnableButtonMessage),
        value: "auto_collect_enable",
        disabled:
          invoice.payments_invoice_auto_collection_status ===
          PaymentsInvoiceAutoCollectionStatus.Failed
      });
    }
    actions.push({
      label: "View PDF",
      value: "view_pdf",
      disabled: false
    });
    if (
      process.env.REACT_APP_ADYEN_ENVIRONMENT !== "live" &&
      invoice.status !== InvoiceStatus.Paid &&
      invoice.status !== InvoiceStatus.Voided
    ) {
      actions.push({
        label: "Pay Now",
        value: "pay_now",
        disabled: false
      });
    }
    actions.push({
      label: "Collect Now",
      value: "collect_now",
      disabled: invoice.status === InvoiceStatus.Paid || invoice.status === InvoiceStatus.Voided,
      disabledMessage:
        invoice.status === InvoiceStatus.Paid
          ? "Invoice is paid"
          : invoice.status === InvoiceStatus.Voided
            ? "Invoice is voided"
            : "",
      disabledMessagePosition: "top"
    });
    actions.push({
      label: "Sync Invoice",
      value: "sync_invoice",
      disabled: false
    });

    const hasValidTransactionId = !!invoice.adyen_transaction?.id;
    const isPaidOrHasTransaction = invoice.status === InvoiceStatus.Paid || hasValidTransactionId;

    actions.push({
      label: "Show Receipt",
      value: "show_receipt",
      disabled: !isPaidOrHasTransaction,
      disabledMessage: !hasValidTransactionId
        ? "No receipt available"
        : "Invoice must be paid to view receipt",
      disabledMessagePosition: "top"
    });

    return actions;
  };

  return (
    <div>
      {informationDialogue.open && (
        <div className="z-40">
          <div className="fixed top-0 left-0 flex items-center justify-center w-screen h-[44rem] opacity-100 z-20">
            <div
              className="bg-white flex flex-col rounded shadow-lg max-w-[48rem] p-8"
              ref={informationDialogueRef}
            >
              <div className="flex space-x-4">
                <InfoIcon className="h-6 w-6 text-blue-600" />
                <div className="flex flex-col space-y-4 text-wrap">
                  <div>{informationDialogue.message && informationDialogue.message}</div>
                  <button
                    className="self-end bg-blue-600 text-white hover:bg-blue-500 px-4 py-2 rounded"
                    onClick={() => {
                      setInformationDialogue({
                        open: false
                      });
                    }}
                  >
                    Ok
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
        </div>
      )}
      {collectNowModal.open && (
        <div className="z-40">
          <div className="fixed top-0 left-0 flex justify-center w-screen opacity-100 z-20 h-[42rem]">
            <div
              className="relative bg-white flex flex-col rounded shadow-lg max-w-[48rem] min-w-[23rem] mt-28"
              ref={collectNowModalRef}
            >
              <div className="flex items-center justify-between p-4 border-b">
                <div className="font-semibold">Collect Now</div>
                <button
                  onClick={() => {
                    setCollectNowModal({
                      open: false,
                      isLoading: false
                    });
                    onClose();
                  }}
                >
                  <CloseIcon className="h-3 w-3 text-gray-700" />
                </button>
              </div>
              <div className="p-4 flex flex-col text-left space-y-4 flex-grow">
                <div>Available Payment Methods</div>
                {collectNowModal.isLoading ? (
                  <div className="flex flex-grow items-center">
                    <LoadingPage />
                  </div>
                ) : collectNowAvailablePaymentMethods.length > 0 ? (
                  <div className="flex-grow h-[21.9rem] overflow-y-auto">
                    {collectNowAvailablePaymentMethods.map((method, index) => (
                      <div
                        className={`relative flex items-center justify-between cursor-pointer ${
                          index !== collectNowAvailablePaymentMethods.length - 1 ? "border-b" : ""
                        }`}
                        key={method.key}
                        onClick={() =>
                          setCollectNowModal({
                            ...collectNowModal,
                            selectedToken: method.key
                          })
                        }
                      >
                        <div className="flex items-center">
                          <input
                            type="radio"
                            checked={method.key === collectNowModal.selectedToken}
                            onChange={() => {}}
                          />
                          <div className="flex items-center p-2" key={method.key}>
                            <img src={method.logoSrc} alt={method.displayName} className="mr-4" />
                            <div className="flex flex-col">
                              <span>{method.detailsDisplayLine1}</span>
                              <span>{method.detailsDisplayLine2}</span>
                            </div>
                            {method.primary && (
                              <div className="py-1 px-2 mx-4 mb-4 bg-blue-100 text-blue-600 border border-blue-400 text-xs rounded">
                                Primary
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <NoAvailablePaymentMethod className="self-center" />
                    <div className="text-center">Client has not added any payment method</div>
                  </>
                )}
              </div>
              <div className="w-full flex justify-end p-4 border-t mt-4">
                <button
                  className={`text-white px-4 py-2 rounded ${
                    !collectNowModal.selectedToken
                      ? "cursor-not-allowed bg-gray-400"
                      : "bg-blue-600 hover:bg-blue-500"
                  }`}
                  disabled={!collectNowModal.selectedToken}
                  onClick={onCollectNowRequest}
                >
                  Collect Now
                </button>
              </div>
            </div>
          </div>
          <div className="fixed top-0 left-0 w-screen h-screen z-10 flex items-center justify-center bg-gray-950 opacity-20" />
        </div>
      )}
      <div
        className="px-4 py-2 flex flex-col items-center"
        onClick={() => (open ? onClose() : onOpen())}
      >
        <Dropdown
          options={actions()}
          onChange={onChange}
          position="bottom-right"
          anchor={
            <div className="cursor-pointer flex flex-col items-center">
              <KebabMenuIcon className={`${open ? "text-blue-400" : "text-blue-600"}`} />
              <div className={`${open ? "text-blue-400" : "text-blue-600"}`}>More</div>
            </div>
          }
        />
      </div>
    </div>
  );
};

export default InvoiceListActions;
